import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { api, handleApiError } from '@/services/client';
import { ConfirmDialog } from '../components/ui/Modal';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download,
  FileText,
  FileSpreadsheet,
  FileJson,
  FileImage,
  FileCode,
  File,
  Settings,
  Calendar,
  Clock,
  Check,
  ChevronDown,
  ChevronRight,
  Folder,
  History,
  Search,
  Filter,
  Trash2,
  RefreshCw,
  ExternalLink,
  Copy,
  MoreVertical,
} from 'lucide-react';

// Types
type ExportFormat = 'pdf' | 'excel' | 'word' | 'csv' | 'json' | 'html' | 'markdown' | 'png';
type ExportScope = 'all' | 'tabs' | 'sessions' | 'bookmarks' | 'research';

interface ExportConfig {
  id: string;
  name: string;
  format: ExportFormat;
  scope: ExportScope;
  filters: ExportFilter[];
  schedule?: ExportSchedule;
  lastExport: Date | null;
  exportCount: number;
}

// Formats the backend can render today. Others are shown as roadmap ("Soon").
const SUPPORTED_FORMATS: ExportFormat[] = ['csv', 'json', 'html', 'markdown'];

interface ExportFilter {
  field: string;
  operator: 'equals' | 'contains' | 'greaterThan' | 'lessThan';
  value: string;
}

interface ExportSchedule {
  frequency: 'once' | 'daily' | 'weekly' | 'monthly';
  time: string;
  recipients?: string[];
}

interface ExportHistoryItem {
  id: string;
  configName: string;
  format: string;
  createdAt: Date;
  size: string;
  status: 'completed' | 'processing' | 'failed';
  downloadUrl?: string;
}

// Format icons
const formatIcons: Record<string, React.ReactNode> = {
  pdf: <FileText size={20} className="text-red-400" />,
  excel: <FileSpreadsheet size={20} className="text-emerald-400" />,
  word: <FileText size={20} className="text-sky-400" />,
  csv: <FileSpreadsheet size={20} className="text-success-400" />,
  json: <FileCode size={20} className="text-yellow-400" />,
  html: <FileCode size={20} className="text-blue-400" />,
  markdown: <FileText size={20} className="text-khiimori-text-muted" />,
  png: <FileImage size={20} className="text-purple-400" />,
};

export const ExportCenterPage: React.FC = () => {
  // State
  const [exports, setExports] = useState<ExportConfig[]>([]);
  const [history, setHistory] = useState<ExportHistoryItem[]>([]);
  const [activeTab, setActiveTab] = useState<'configs' | 'history' | 'schedules'>('configs');
  const [isCreating, setIsCreating] = useState(false);
  const [selectedExport, setSelectedExport] = useState<ExportConfig | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedExportId, setSelectedExportId] = useState<string | null>(null);

  // Builder state
  const [builderName, setBuilderName] = useState('');
  const [builderFormat, setBuilderFormat] = useState<ExportConfig['format']>('json');
  const [builderScope, setBuilderScope] = useState<ExportConfig['scope']>('all');
  const [builderSchedule, setBuilderSchedule] = useState<ExportSchedule | undefined>(undefined);
  const [hasSchedule, setHasSchedule] = useState(false);

  // Quick export state
  const [showQuickExport, setShowQuickExport] = useState(false);
  const [quickDataType, setQuickDataType] = useState('Tabs');
  const [quickFormat, setQuickFormat] = useState<ExportConfig['format']>('json');
  const [quickExporting, setQuickExporting] = useState(false);
  const [quickExportError, setQuickExportError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const mapConfigFromApi = (cfg: Record<string, unknown>): ExportConfig => ({
    id: String(cfg.id),
    name: String(cfg.name ?? 'Export'),
    format: (cfg.format as ExportConfig['format']) ?? 'json',
    scope: (cfg.scope as ExportConfig['scope']) ?? 'all',
    filters: (cfg.filters as ExportFilter[]) ?? [],
    schedule: cfg.schedule as ExportSchedule | undefined,
    lastExport: cfg.last_export || cfg.lastExport ? new Date(String(cfg.last_export ?? cfg.lastExport)) : null,
    exportCount: Number(cfg.export_count ?? cfg.exportCount ?? 0),
  });

  const mapHistoryFromApi = (item: Record<string, unknown>): ExportHistoryItem => ({
    id: String(item.id),
    configName: String(item.config_name ?? item.configName ?? 'Export'),
    format: String(item.format ?? 'json'),
    createdAt: new Date(String(item.created_at ?? item.createdAt ?? Date.now())),
    size: String(item.size ?? '—'),
    status: (item.status as ExportHistoryItem['status']) ?? 'completed',
    downloadUrl: item.download_url ? String(item.download_url) : undefined,
  });

  // Fetch export configs and history on mount
  useEffect(() => {
    api.get('/export/configs')
      .then((r) => {
        const rows = r.data?.configs ?? r.data ?? [];
        setExports(
          rows.map((cfg: Record<string, unknown>) => mapConfigFromApi(cfg)),
        );
      })
      .catch(console.error);
    api.get('/export/history')
      .then((r) => {
        const rows = r.data?.history ?? r.data ?? [];
        setHistory(
          rows.map((item: Record<string, unknown>) => mapHistoryFromApi(item)),
        );
      })
      .catch(() => {});
  }, []);

  // Format options (per USER_FLOWS §10.1). Formats not yet renderable by the
  // backend are flagged so the UI shows the full roadmap without breaking.
  const formats = [
    { id: 'pdf', label: 'PDF Document', icon: formatIcons.pdf, description: 'Formatted report for sharing and printing' },
    { id: 'excel', label: 'Excel Workbook', icon: formatIcons.excel, description: 'Data tables for spreadsheets' },
    { id: 'word', label: 'Word Document', icon: formatIcons.word, description: 'Editable rich-text document' },
    { id: 'csv', label: 'CSV Spreadsheet', icon: formatIcons.csv, description: 'Compatible with Excel and Sheets' },
    { id: 'json', label: 'JSON Data', icon: formatIcons.json, description: 'Machine-readable structured data' },
    { id: 'markdown', label: 'Markdown', icon: formatIcons.markdown, description: 'Lightweight plain text with formatting' },
    { id: 'html', label: 'HTML File', icon: formatIcons.html, description: 'Web-ready self-contained page' },
  ].map((f) => ({ ...f, supported: SUPPORTED_FORMATS.includes(f.id as ExportFormat) }));

  const quickFormats = useMemo(
    () => formats.filter((f) => f.supported),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const openQuickExport = () => {
    setQuickExportError(null);
    setExportError(null);
    setQuickDataType('Tabs');
    setQuickFormat('json');
    setShowQuickExport(true);
  };

  // Source / scope options — what to export (USER_FLOWS §10.1 "picks a source").
  const scopeOptions = [
    { id: 'all', label: 'All Data', description: 'Tabs and workspace sessions together' },
    { id: 'tabs', label: 'Tabs', description: 'Every open tab in your workspace' },
    { id: 'sessions', label: 'Sessions', description: 'Workspace sessions and their metadata' },
    { id: 'bookmarks', label: 'Bookmarks', description: 'Saved tabs and bookmarked pages' },
    { id: 'research', label: 'Research', description: 'Research syntheses and cited sources' },
  ];

  // Filter exports by search
  const filteredExports = useMemo(() => {
    if (!searchQuery) return exports;
    return exports.filter(exp =>
      exp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exp.format.includes(searchQuery)
    );
  }, [exports, searchQuery]);

  // Save export config
  const saveExport = () => {
    if (!builderName) return;
    setExportError(null);

    api.post('/export/configs', {
      name: builderName,
      format: builderFormat,
      scope: builderScope,
      schedule: hasSchedule ? builderSchedule : undefined,
    }).then(r => {
      setExports([mapConfigFromApi(r.data as Record<string, unknown>), ...exports]);
      resetBuilder();
      setIsCreating(false);
    }).catch((err) => {
      setExportError(handleApiError(err));
    });
  };

  // Reset builder
  const resetBuilder = () => {
    setBuilderName('');
    setBuilderFormat('json');
    setBuilderScope('all');
    setBuilderSchedule(undefined);
    setHasSchedule(false);
  };

  // Run export
  const runExport = (exp: ExportConfig) => {
    setExportError(null);
    api.post('/export/execute', { config_id: exp.id })
      .then((r) => {
        const newHistoryItem = mapHistoryFromApi({
          ...r.data,
          config_name: exp.name,
        } as Record<string, unknown>);
        setHistory([newHistoryItem, ...history]);
        setExports(exports.map(e =>
          e.id === exp.id ? { ...e, lastExport: new Date(), exportCount: e.exportCount + 1 } : e
        ));
        setActiveTab('history');
      })
      .catch((err) => {
        setExportError(handleApiError(err));
      });
  };

  const downloadHistoryItem = useCallback(async (item: ExportHistoryItem) => {
    try {
      const res = await api.get(`/export/download/${item.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${item.configName.replace(/\s+/g, '-').toLowerCase()}.${item.format === 'markdown' ? 'md' : item.format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setExportError(handleApiError(err));
    }
  }, []);

  const runQuickExport = async () => {
    if (quickExporting) return;
    setQuickExportError(null);
    setExportError(null);
    setQuickExporting(true);
    try {
      const r = await api.post('/export/quick', { data_type: quickDataType, format: quickFormat });
      const item = mapHistoryFromApi(r.data as Record<string, unknown>);
      setHistory((prev) => [item, ...prev]);
      setShowQuickExport(false);
      setActiveTab('history');
      await downloadHistoryItem(item);
    } catch (err) {
      const message = handleApiError(err);
      setQuickExportError(message);
      setExportError(message);
    } finally {
      setQuickExporting(false);
    }
  };

  // Delete export config
  const deleteExport = (id: string) => {
    setSelectedExportId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteExport = () => {
    if (!selectedExportId) return;
    api.delete(`/export/configs/${selectedExportId}`)
      .then(() => {
        setExports(exports.filter(exp => exp.id !== selectedExportId));
        if (selectedExport?.id === selectedExportId) setSelectedExport(null);
        setShowDeleteConfirm(false);
        setSelectedExportId(null);
      })
      .catch(console.error);
  };

  // Format date
  const formatDate = (date: Date | null) => {
    if (!date) return 'Never';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Get schedule label
  const getScheduleLabel = (schedule?: ExportSchedule) => {
    if (!schedule) return null;
    return `${schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1)} at ${schedule.time}`;
  };

  return (
    <div className="h-full flex flex-col bg-khiimori-bg-secondary">
      {/* Header */}
      <div className="border-b border-khiimori-border p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-khiimori-text-primary">Export Center</h1>
            <p className="text-sm text-khiimori-text-muted mt-1">Manage your data exports and downloads</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={openQuickExport}
              className="px-4 py-2 bg-khiimori-bg-elevated text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-colors flex items-center gap-2"
            >
              <Download size={16} />
              Quick Export
            </button>
            <button
              onClick={() => setIsCreating(true)}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-400 transition-colors flex items-center gap-2"
            >
              <File size={16} />
              New Export
            </button>
          </div>
        </div>

        {/* Search and filters */}
        <div className="mt-4 flex items-center gap-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-khiimori-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search exports..."
              className="w-full pl-10 pr-4 py-2 bg-khiimori-bg-elevated border border-khiimori-border rounded-lg text-khiimori-text-primary placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>

        {exportError && (
          <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">
            {exportError}
          </div>
        )}

        {/* Tab navigation */}
        <div className="flex gap-4 mt-4 border-b border-khiimori-border">
          {[
            { id: 'configs', label: 'Export Configs', count: exports.length },
            { id: 'history', label: 'History', count: history.length },
            { id: 'schedules', label: 'Scheduled', count: exports.filter(e => e.schedule).length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`pb-3 px-1 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'text-purple-400 border-b-2 border-purple-400'
                  : 'text-khiimori-text-muted hover:text-khiimori-text-secondary'
              }`}
            >
              {tab.label}
              <span className="bg-khiimori-bg-elevated text-khiimori-text-muted text-xs px-2 py-0.5 rounded-full">{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <AnimatePresence mode="wait">
          {activeTab === 'configs' && (
            <motion.div
              key="configs"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {filteredExports.length === 0 ? (
                <div className="text-center py-12">
                  <File size={48} className="mx-auto text-khiimori-text-muted mb-4" />
                  <h3 className="text-lg font-medium text-khiimori-text-secondary">No export configs</h3>
                  <p className="text-khiimori-text-muted mt-2">Create your first export configuration</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredExports.map(exp => (
                    <motion.div
                      key={exp.id}
                      layout
                      className="bg-khiimori-bg-elevated rounded-xl p-4 border border-khiimori-border hover:border-khiimori-border transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-khiimori-bg-tertiary rounded-lg">
                            {formatIcons[exp.format]}
                          </div>
                          <div>
                            <h3 className="font-semibold text-khiimori-text-primary">{exp.name}</h3>
                            <div className="flex items-center gap-3 mt-1 text-sm text-khiimori-text-muted">
                              <span className="uppercase">{exp.format}</span>
                              <span>•</span>
                              <span>{exp.scope}</span>
                            </div>
                            {exp.schedule && (
                              <div className="mt-2 flex items-center gap-2 text-xs text-purple-400">
                                <Clock size={12} />
                                {getScheduleLabel(exp.schedule)}
                              </div>
                            )}
                            <div className="mt-2 text-xs text-khiimori-text-muted">
                              Last export: {formatDate(exp.lastExport)} • {exp.exportCount} exports
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => runExport(exp)}
                            className="px-3 py-1.5 bg-success-500 text-khiimori-text-primary text-sm rounded-lg hover:bg-success-400 transition-colors flex items-center gap-1"
                          >
                            <Download size={14} />
                            Export
                          </button>
                          <button
                            onClick={() => deleteExport(exp.id)}
                            className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors"
                          >
                            <Trash2 size={16} className="text-red-400" />
                          </button>
                          <button className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors">
                            <MoreVertical size={16} className="text-khiimori-text-muted" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'history' && (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {history.length === 0 ? (
                <div className="text-center py-12">
                  <History size={48} className="mx-auto text-khiimori-text-muted mb-4" />
                  <h3 className="text-lg font-medium text-khiimori-text-secondary">No export history</h3>
                  <p className="text-khiimori-text-muted mt-2">Run an export to see completed downloads here</p>
                </div>
              ) : (
              <div className="space-y-3">
                {history.map(item => (
                  <motion.div
                    key={item.id}
                    layout
                    className="bg-khiimori-bg-elevated rounded-xl p-4 border border-khiimori-border flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-khiimori-bg-tertiary rounded-lg">
                        {formatIcons[item.format]}
                      </div>
                      <div>
                        <h3 className="font-medium text-khiimori-text-primary">{item.configName}</h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-khiimori-text-muted">
                          <span className="uppercase">{item.format}</span>
                          <span>•</span>
                          <span>{item.size}</span>
                          <span>•</span>
                          <span>{formatDate(item.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${
                        item.status === 'completed' ? 'text-success-400' :
                        item.status === 'processing' ? 'text-blue-400' : 'text-red-400'
                      }`}>
                        {item.status}
                      </span>
                      {item.status === 'completed' && (
                        <button
                          onClick={() => downloadHistoryItem(item)}
                          className="px-3 py-1.5 bg-khiimori-bg-tertiary text-khiimori-text-primary text-sm rounded-lg hover:bg-khiimori-bg-tertiary transition-colors flex items-center gap-1"
                        >
                          <Download size={14} />
                          Download
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              )}
            </motion.div>
          )}

          {activeTab === 'schedules' && (
            <motion.div
              key="schedules"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {exports.filter(e => e.schedule).length === 0 ? (
                <div className="text-center py-12">
                  <Calendar size={48} className="mx-auto text-khiimori-text-muted mb-4" />
                  <h3 className="text-lg font-medium text-khiimori-text-secondary">No scheduled exports</h3>
                  <p className="text-khiimori-text-muted mt-2">Enable scheduling when creating an export</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {exports.filter(e => e.schedule).map(exp => (
                    <motion.div
                      key={exp.id}
                      layout
                      className="bg-khiimori-bg-elevated rounded-xl p-4 border border-khiimori-border"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-khiimori-text-primary">{exp.name}</h3>
                          <div className="mt-1 flex items-center gap-2 text-sm text-purple-400">
                            <Clock size={14} />
                            {getScheduleLabel(exp.schedule)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors">
                            <RefreshCw size={16} className="text-khiimori-text-muted" />
                          </button>
                          <button className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors">
                            <Settings size={16} className="text-khiimori-text-muted" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Quick Export Modal */}
      {showQuickExport && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-6 modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget && !quickExporting) setShowQuickExport(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="quick-export-title"
        >
          <div
            className="modal-panel w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-khiimori-border">
              <div className="flex items-center justify-between">
                <h2 id="quick-export-title" className="text-xl font-bold text-khiimori-text-primary">Quick Export</h2>
                <button
                  type="button"
                  onClick={() => setShowQuickExport(false)}
                  disabled={quickExporting}
                  className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors disabled:opacity-50"
                >
                  <ChevronDown size={18} className="text-khiimori-text-muted" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {quickExportError && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">
                  {quickExportError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Select Data Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {['Tabs', 'Sessions', 'Bookmarks', 'Research', 'All Data'].map(type => (
                    <button
                      key={type}
                      type="button"
                      disabled={quickExporting}
                      onClick={() => setQuickDataType(type)}
                      className={`p-3 border rounded-lg text-sm transition-colors disabled:opacity-50 ${
                        quickDataType === type
                          ? 'border-purple-500 bg-purple-500/10 text-khiimori-text-primary'
                          : 'bg-khiimori-bg-tertiary border-khiimori-border text-khiimori-text-secondary hover:border-purple-500'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Export Format</label>
                <div className="grid grid-cols-2 gap-2">
                  {quickFormats.map(fmt => (
                    <button
                      key={fmt.id}
                      type="button"
                      disabled={quickExporting}
                      onClick={() => setQuickFormat(fmt.id as ExportConfig['format'])}
                      className={`p-3 border rounded-lg text-center transition-colors disabled:opacity-50 ${
                        quickFormat === fmt.id
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'bg-khiimori-bg-tertiary border-khiimori-border hover:border-purple-500'
                      }`}
                    >
                      <div className="mb-1">{fmt.icon}</div>
                      <div className="text-xs text-khiimori-text-secondary">{fmt.label.split(' ')[0]}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-khiimori-border flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowQuickExport(false)}
                disabled={quickExporting}
                className="px-4 py-2 bg-khiimori-bg-tertiary text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void runQuickExport()}
                disabled={quickExporting}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-400 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={16} />
                {quickExporting ? 'Exporting…' : 'Export Now'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Create Export Modal */}
      <AnimatePresence>
        {isCreating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-6"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-khiimori-bg-elevated rounded-2xl w-full max-w-lg overflow-hidden"
            >
              <div className="p-6 border-b border-khiimori-border">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-khiimori-text-primary">Create Export Configuration</h2>
                  <button
                    onClick={() => { setIsCreating(false); resetBuilder(); }}
                    className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors"
                  >
                    <ChevronDown size={18} className="text-khiimori-text-muted" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Name</label>
                  <input
                    type="text"
                    value={builderName}
                    onChange={(e) => setBuilderName(e.target.value)}
                    placeholder="e.g., Weekly Summary"
                    className="w-full px-4 py-2 bg-khiimori-bg-tertiary border border-khiimori-border rounded-lg text-khiimori-text-primary placeholder-gray-400 focus:outline-none focus:border-purple-500"
                  />
                </div>

                {/* Format */}
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Format</label>
                  <div className="grid grid-cols-3 gap-2">
                    {formats.map(fmt => (
                      <button
                        key={fmt.id}
                        type="button"
                        disabled={!fmt.supported}
                        title={fmt.supported ? fmt.description : `${fmt.label} export is coming soon`}
                        onClick={() => fmt.supported && setBuilderFormat(fmt.id as ExportFormat)}
                        className={`relative p-3 rounded-lg border text-center transition-colors ${
                          !fmt.supported
                            ? 'border-khiimori-border bg-khiimori-bg-tertiary/40 opacity-50 cursor-not-allowed'
                            : builderFormat === fmt.id
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-khiimori-border bg-khiimori-bg-tertiary hover:border-primary-500'
                        }`}
                      >
                        {!fmt.supported && (
                          <span className="absolute top-1 right-1 text-[9px] font-semibold uppercase tracking-wide text-amber-400/90 bg-amber-500/10 rounded px-1">Soon</span>
                        )}
                        <div className="mb-1">{fmt.icon}</div>
                        <div className="text-xs text-khiimori-text-primary">{fmt.label.split(' ')[0]}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Scope */}
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Data Scope</label>
                  <div className="space-y-2">
                    {scopeOptions.map(option => (
                      <button
                        key={option.id}
                        onClick={() => setBuilderScope(option.id as ExportConfig['scope'])}
                        className={`w-full p-3 rounded-lg border text-left transition-colors ${
                          builderScope === option.id
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-khiimori-border bg-khiimori-bg-tertiary hover:border-primary-500'
                        }`}
                      >
                        <div className="text-sm font-medium text-khiimori-text-primary">{option.label}</div>
                        <div className="text-xs text-khiimori-text-muted">{option.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Schedule */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasSchedule}
                      onChange={(e) => {
                        setHasSchedule(e.target.checked);
                        if (e.target.checked && !builderSchedule) {
                          setBuilderSchedule({ frequency: 'weekly', time: '09:00' });
                        }
                      }}
                      className="w-4 h-4 rounded border-khiimori-border bg-khiimori-bg-tertiary text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-khiimori-text-secondary">Enable scheduling</span>
                  </label>
                  {hasSchedule && (
                    <div className="mt-3 flex gap-3">
                      <select
                        value={builderSchedule?.frequency || 'weekly'}
                        onChange={(e) => setBuilderSchedule({ ...builderSchedule!, frequency: e.target.value as any })}
                        className="flex-1 px-3 py-2 bg-khiimori-bg-tertiary border border-khiimori-border rounded-lg text-khiimori-text-primary text-sm"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                      <input
                        type="time"
                        value={builderSchedule?.time || '09:00'}
                        onChange={(e) => setBuilderSchedule({ ...builderSchedule!, time: e.target.value })}
                        className="px-3 py-2 bg-khiimori-bg-tertiary border border-khiimori-border rounded-lg text-khiimori-text-primary text-sm"
                      />
                    </div>
                  )}
                </div>

                <p className="text-xs text-khiimori-text-muted leading-relaxed border-t border-khiimori-border/60 pt-4">
                  The export is rendered in your chosen format and offered as a download. Large exports are prepared in the background and appear under <span className="text-khiimori-text-secondary">History</span> when ready, where they stay available for re-download.
                </p>
              </div>

              <div className="p-6 border-t border-khiimori-border flex justify-end gap-3">
                <button
                  onClick={() => { setIsCreating(false); resetBuilder(); }}
                  className="px-4 py-2 bg-khiimori-bg-tertiary text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveExport}
                  disabled={!builderName}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Export
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteExport}
        title="Delete Export"
        message="Are you sure you want to delete this export configuration? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};